import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import {
  BooleanInput,
  CheckboxGroupInput,
  required,
  TextInput,
  useGetList,
  useTranslate,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import { useQuery } from 'react-query';

import {
  PostB2bIndividualOperationArbitrageBody,
  PostB2bIndividualOperationArbitrageSummaryResponse,
} from '@Common/types/bo/b2b/individual_operations/arbitrage';
import { B2bCompanyPlanTypeParameters } from '@Common/types/bo/b2b/plans';
import { GetInstrumentsResponseItem } from '@Common/types/bo/instruments';

import {
  ArbitrageFormData,
  buildArbitrageBody,
  sumArbitrageRows,
} from '@sh4res/bo-common/components/B2bArbitrage/buildArbitrageBody';
import { useArbitrageErrorMessage } from '@sh4res/bo-common/components/B2bArbitrage/useArbitrageErrorMessage';
import { ShowSection } from '@sh4res/bo-common/components/ShowSection';
import { useCreatePromise } from '@sh4res/bo-common/hooks';
import { formatCentsNumber } from '@sh4res/bo-common/utils';

const testId = 'BoExternalB2bArbitrageCreateConfirmationStep';

type SummaryAllocation =
  PostB2bIndividualOperationArbitrageSummaryResponse['arbitrages'][number]['allocations'][number];

const SummaryAllocations = ({ allocations }: { allocations: SummaryAllocation[] }) => {
  const translate = useTranslate();
  const instrumentIds = useMemo(
    () => [...new Set(allocations.map((allocation) => allocation.instrument_id))],
    [allocations],
  );
  const { data: instruments = [] } = useGetList<GetInstrumentsResponseItem>(
    'instruments',
    { filter: { id: instrumentIds }, pagination: { page: 1, perPage: 100 } },
    { enabled: instrumentIds.length > 0 },
  );
  const getInstrumentName = (instrumentId: number) => {
    const instrument = instruments.find(({ id }) => id === instrumentId);
    return instrument ? instrument.name || instrument.symbol : `#${instrumentId}`;
  };

  return (
    <>
      {(['sell', 'buy'] as const).map((side) => (
        <Box key={side} sx={{ marginTop: 1 }}>
          <Typography variant="subtitle2">
            {translate(
              `resources.bo_common.b2b.arbitrage_create.sections.${side === 'sell' ? 'sources' : 'targets'}`,
            )}
          </Typography>
          {allocations
            .filter((allocation) => allocation.side === side)
            .map((allocation) => (
              <Typography
                key={`${allocation.saving_plan_id}-${allocation.instrument_id}`}
                variant="body2"
                data-testid={`${testId}_${side}Allocation`}>
                {getInstrumentName(allocation.instrument_id)} ·{' '}
                {B2bCompanyPlanTypeParameters[allocation.saving_plan_type].name} ·{' '}
                {formatCentsNumber(Math.abs(allocation.amount_gross.value))}
              </Typography>
            ))}
        </Box>
      ))}
    </>
  );
};

export const ConfirmationStep = () => {
  const translate = useTranslate();
  const getArbitrageErrorMessage = useArbitrageErrorMessage();

  const [employeeId, sources, targets]: [
    ArbitrageFormData['b2b_employee_id'],
    ArbitrageFormData['sources'],
    ArbitrageFormData['targets'],
  ] = useWatch({ name: ['b2b_employee_id', 'sources', 'targets'] });

  const totalSold = sumArbitrageRows(sources);
  const totalBought = sumArbitrageRows(targets);

  const body = useMemo(
    () => buildArbitrageBody({ b2b_employee_id: employeeId, sources, targets }),
    [employeeId, sources, targets],
  );

  const [getSummary] = useCreatePromise<
    PostB2bIndividualOperationArbitrageBody,
    PostB2bIndividualOperationArbitrageSummaryResponse
  >();

  const { data: summary, error } = useQuery(
    ['arbitrageSummary', body],
    async () =>
      getSummary('b2b/individual_operations/summary', { data: { type: 'arbitrage', ...body } }),
    { enabled: employeeId !== null && totalSold === totalBought && totalSold > 0, retry: false },
  );

  return (
    <>
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.arbitrage_create.sections.summary_confirmation"
        noCard={true}>
        <Box>
          <Typography>
            {translate('resources.bo_common.b2b.arbitrage_create.fields.total_sold')}:{' '}
            {formatCentsNumber(totalSold)}
          </Typography>
          <Typography>
            {translate('resources.bo_common.b2b.arbitrage_create.fields.total_bought')}:{' '}
            {formatCentsNumber(totalBought)}
          </Typography>
          {totalSold !== totalBought && (
            <Typography color="error" data-testid={`${testId}_AmountsDoNotMatch`}>
              {translate('resources.bo_common.b2b.arbitrage_create.messages.amounts_do_not_match')}
            </Typography>
          )}
          {summary && (
            <SummaryAllocations
              allocations={summary.arbitrages.flatMap((arbitrage) => arbitrage.allocations)}
            />
          )}
          {error ? (
            <Typography color="error" data-testid={`${testId}_SummaryError`}>
              {getArbitrageErrorMessage(error)}
            </Typography>
          ) : null}
        </Box>
      </ShowSection>
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.arbitrage_create.sections.confirmations"
        noCard={true}>
        <TextInput
          data-testid={`${testId}_ReasonInput`}
          source="reason"
          label="resources.bo_common.b2b.arbitrage_create.fields.reason"
          multiline
          fullWidth
          isRequired
          validate={[required()]}
        />
        <BooleanInput
          data-testid={`${testId}_NotifyEmployeeInput`}
          source="notify_employee"
          defaultValue={true}
          label="resources.bo_common.b2b.arbitrage_create.fields.notify_employee"
          helperText="resources.bo_common.b2b.arbitrage_create.messages.notify_employee_help"
        />
        <CheckboxGroupInput
          data-testid={`${testId}_ConfirmAccuracyInput`}
          source="confirm_accuracy"
          sx={{ marginLeft: '5px' }}
          label={false}
          isRequired
          validate={[required()]}
          choices={[
            {
              id: 'yes',
              name: 'resources.bo_common.b2b.arbitrage_create.sections.confirm_accuracy',
            },
          ]}
        />
      </ShowSection>
    </>
  );
};
